import { StandardEvents } from '@shopify/events';

const CART_COUNT_SELECTOR = '[data-bn-cart-count]';

const CATEGORY_SEARCH_COLLECTIONS = {
  cats: '228876-מזון-יבש-לחתול',
  dogs: '227507-מזון-יבש-לכלב',
};

const SEARCH_FOOD_TERMS = new Set(['אוכל', 'מזון']);
const SEARCH_DRY_TERMS = new Set(['יבש', 'יבשה']);
const SEARCH_CAT_TERMS = new Set(['חתול', 'חתולים', 'לחתול', 'לחתולים']);
const SEARCH_DOG_TERMS = new Set(['כלב', 'כלבים', 'לכלב', 'לכלבים']);
const SEARCH_CATEGORY_TERMS = new Set([
  ...SEARCH_FOOD_TERMS,
  ...SEARCH_DRY_TERMS,
  ...SEARCH_CAT_TERMS,
  ...SEARCH_DOG_TERMS,
]);

const normalizeSearchQuery = (query) =>
  String(query || '')
    .normalize('NFKC')
    .toLocaleLowerCase('he')
    .replace(/[\u05f3\u05f4'"‘’“”`.,!?():;\-_/\\]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const categoryCollectionForQuery = (query) => {
  const terms = normalizeSearchQuery(query).split(' ').filter(Boolean);

  if (!terms.length || terms.some((term) => !SEARCH_CATEGORY_TERMS.has(term))) return null;

  const describesFood = terms.some((term) => SEARCH_FOOD_TERMS.has(term));
  const describesDryFood = terms.some((term) => SEARCH_DRY_TERMS.has(term));
  const describesCats = terms.some((term) => SEARCH_CAT_TERMS.has(term));
  const describesDogs = terms.some((term) => SEARCH_DOG_TERMS.has(term));

  if (!describesFood || !describesDryFood || describesCats === describesDogs) return null;

  return describesCats ? CATEGORY_SEARCH_COLLECTIONS.cats : CATEGORY_SEARCH_COLLECTIONS.dogs;
};

const storefrontPath = (path) => {
  const root = window.Shopify?.routes?.root || '/';
  return `${root.replace(/\/?$/, '/')}${path.replace(/^\//, '')}`;
};

const categorySearchDestination = (query) => {
  const collectionHandle = categoryCollectionForQuery(query);
  return collectionHandle ? storefrontPath(`collections/${collectionHandle}`) : null;
};

document.addEventListener(
  'submit',
  (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || form.method.toLowerCase() !== 'get') return;

    const formAction = new URL(form.action, window.location.href);
    if (!formAction.pathname.endsWith('/search')) return;

    const queryInput = form.elements.namedItem('q');
    if (!(queryInput instanceof HTMLInputElement)) return;

    const destination = categorySearchDestination(queryInput.value);
    if (!destination) return;

    event.preventDefault();
    window.location.assign(destination);
  },
  true
);

const currentUrl = new URL(window.location.href);
if (currentUrl.pathname.endsWith('/search')) {
  const destination = categorySearchDestination(currentUrl.searchParams.get('q'));
  if (destination) window.location.replace(destination);
}

const renderCartCount = (itemCount) => {
  const count = Number.isFinite(Number(itemCount)) ? Number(itemCount) : 0;
  document.querySelectorAll(CART_COUNT_SELECTOR).forEach((element) => {
    element.textContent = count > 99 ? '99+' : String(count);
    element.setAttribute('aria-label', `${element.dataset.bnCartLabel || 'Cart items'}: ${count}`);
  });
};

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  event.promise
    ?.then(({ cart, detail }) => {
      renderCartCount(cart?.totalQuantity ?? detail?.itemCount ?? 0);
      if (event.action === 'add' && !detail?.didError) {
        const notice = document.querySelector('[data-bn-cart-notice]');
        if (notice) notice.hidden = false;
      }
    })
    .catch((error) => {
      if (error?.name !== 'AbortError') console.warn('[black-natasha-cart-count] Cart update failed:', error);
    });
});

window.addEventListener('pageshow', (event) => {
  if (!event.persisted) return;

  fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { headers: { Accept: 'application/json' } })
    .then((response) => response.json())
    .then((cart) => renderCartCount(cart.item_count))
    .catch(() => {});
});

document.addEventListener('click', (event) => {
  const brandLink = event.target.closest('.bn-brand-filter a');
  if (brandLink) {
    const destination = new URL(brandLink.href);
    const current = new URL(window.location.href);
    current.searchParams.forEach((value, key) => {
      if (key !== 'page') destination.searchParams.append(key, value);
    });
    brandLink.href = destination.href;
  }
  if (event.target.closest('[data-bn-dismiss-notice]')) {
    const notice = document.querySelector('[data-bn-cart-notice]');
    if (notice) notice.hidden = true;
  }
});

const organizeDescriptions = () => {
  document.querySelectorAll('[data-bn-product-content]:not([data-organized])').forEach((section) => {
    const source = section.querySelector('[data-bn-description]');
    if (!source) return;
    section.dataset.organized = 'true';
    const normalizedTitle = section.dataset.productTitle.replace(/\s+/g, ' ').trim();
    source.querySelectorAll('h1,h2,h3,h4').forEach((heading) => {
      if (heading.textContent.replace(/\s+/g, ' ').trim() === normalizedTitle) heading.remove();
    });
    const paragraphs = new Set();
    for (const paragraph of source.querySelectorAll(':scope > p')) {
      if (paragraph.querySelector('img,a,video,iframe,table')) continue;
      const text = paragraph.textContent.replace(/[–—−]/g, '-').replace(/\s+/g, ' ').trim();
      if (text.length < 60) continue;
      if (paragraphs.has(text)) paragraph.remove();
      else paragraphs.add(text);
    }
    const headingPattern = /^(רכיבים|מרכיבים|ערכים תזונתיים|ערך תזונתי|הוראות האכלה|הנחיות האכלה|הוראות שימוש|אופן השימוש|תכולה|מידות|אזהרות)\s*:?$/;
    let body;
    for (const node of [...source.childNodes]) {
      const text = node.textContent.trim();
      if (node instanceof HTMLElement && headingPattern.test(text) && text.length < 40) {
        const disclosure = document.createElement('details');
        disclosure.className = 'bn-product-disclosure';
        disclosure.open = text.includes('אזהרות');
        const summary = document.createElement('summary');
        summary.textContent = text.replace(/:$/, '');
        body = document.createElement('div');
        body.className = 'bn-product-disclosure__body';
        disclosure.append(summary, body);
        node.replaceWith(disclosure);
      } else if (body) {
        body.append(node);
      }
    }
    source.querySelectorAll('details').forEach((details) => {
      const body = details.lastElementChild;
      if (!body.textContent.trim() && !body.querySelector('img,video,table')) details.remove();
    });
  });
};

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;
  const openMenu = event.target.closest('.bn-category-menu[open], .bn-shell-menu[open], .bn-mobile-menu[open]');
  if (openMenu) {
    openMenu.open = false;
    openMenu.querySelector('summary')?.focus();
  }
});

organizeDescriptions();
document.addEventListener('shopify:section:load', organizeDescriptions);
